import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import AppSidebar from "../common/AppSidebar";
import Header from "../../pages/business/header";
import { uploadOrganizationLogo } from "../../setup/api/services/organization";
import { useKcToken } from "../../setup/auth/useKcToken";
import { decodeJwt } from "../../setup/util/decodeToken";
import styles from "./styles.module.scss";

interface TokenClaims {
  given_name?: string;
  family_name?: string;
  email?: string;
}

interface Props {
  /** Organization the logo saves against; omit before an organization exists. */
  organizationCode?: string;
  /** Organization name for the sidebar brand slot; omitted before the first fetch resolves. */
  organizationName?: string;
  /** Organization logo submitted at registration, if any. */
  organizationLogoUrl?: string;
  /**
   * Short review-status line under the organization name (e.g. "قيد المراجعة"). Omitted before
   * an organization exists at all, when there is no status to report.
   */
  organizationStatusLabel?: string;
  children: React.ReactNode;
}

/**
 * Chrome for the organization-gate screens (registration / pending / rejected).
 *
 * Renders the same sidebar and topbar as the rest of the portal — AppSidebar in `inert` mode,
 * since every route still resolves to the gate until an admin approves the organization — so a
 * waiting user sees the real dashboard shell rather than a separate one-off layout.
 */
const OrganizationGateShell: React.FC<Props> = ({
  organizationCode,
  organizationName,
  organizationLogoUrl,
  organizationStatusLabel,
  children,
}) => {
  const { t } = useTranslation();
  const token = useKcToken();

  const claims = decodeJwt<TokenClaims>(token);
  // The gate can't read the profile API's display name the way the app sidebar does, so it
  // falls back to the identity token's claims.
  const displayName =
    [claims?.given_name, claims?.family_name].filter(Boolean).join(" ") ||
    claims?.email ||
    "";

  // Overrides the prop once the user picks a logo, so the new one shows without a refetch.
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null);

  const onLogoChange = useCallback(
    async (file: File) => {
      if (!organizationCode) return;
      setSavedLogoUrl(await uploadOrganizationLogo(organizationCode, file));
    },
    [organizationCode]
  );

  return (
    <>
      <AppSidebar
        inert
        organizationName={
          organizationName || (t("lbl_org_gate_brand_fallback") as string)
        }
        organizationLogoUrl={savedLogoUrl ?? organizationLogoUrl}
        brandSubLabel={organizationStatusLabel}
        userName={displayName}
        userRoleLabel={t("lbl_executive") as string}
        // The logo stays editable while the org is under review: it isn't navigation, so
        // `inert` doesn't apply to it.
        onLogoChange={organizationCode ? onLogoChange : undefined}
      />
      <div className="outletContainer">
        <Header disabled />
        <div className={styles.gateGreet}>
          <b>
            {/* escapeValue off: React escapes on render, so i18next would double-encode any
                &, <, > in the user's name. */}
            {t("lbl_org_gate_greeting", {
              name: displayName,
              interpolation: { escapeValue: false },
            })}
          </b>
          <span>{t("lbl_org_gate_greeting_subtitle")}</span>
        </div>
        {children}
      </div>
    </>
  );
};

export default OrganizationGateShell;
